import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

/**
 * Estado da especificação (o "carrinho" do arquiteto), compartilhado entre o
 * catálogo, o detalhe do produto, a cesta lateral e as telas de especificação.
 *
 * - `lista`: as especificações da pessoa (uma por projeto/cliente).
 * - `ativa`: a especificação em que o "+ Especificar" adiciona (a última usada,
 *   lembrada por pessoa no navegador; se não houver, o rascunho mais recente).
 *   Só especificação montada pelo CATÁLOGO vira ativa — a de PDF enviado não
 *   tem itens (`listaCatalogo` é a lista que as telas de adicionar usam).
 * Tudo fica salvo no banco — começa no celular e termina no computador.
 */
const Ctx = createContext(null);

export function EspecificacaoProvider({ children }) {
  const { user } = useAuth();
  const habilitado = !!user && !user.senha_provisoria && (user.tipo !== 'arquiteto' || !!user.catalogo_visivel);
  const chave = user ? `esp_ativa:${user.tipo}:${user.id}` : null;

  const [lista, setLista] = useState([]);
  const [ativa, setAtiva] = useState(null);
  const [carregandoAtiva, setCarregandoAtiva] = useState(false);
  const [cestaAberta, setCestaAberta] = useState(false);
  const [produtoAdd, setProdutoAdd] = useState(null); // produto sendo especificado
  const [livre, setLivre] = useState(null);           // { item? } — item fora do catálogo
  const [aviso, setAviso] = useState(null);           // { texto, acao? }
  const avisoTimer = useRef(null);
  const ativaRef = useRef(null);
  ativaRef.current = ativa;
  const pendentes = useRef({}); // itemId → { dados, timer } (quantidade digitada/stepper)

  const lembrar = useCallback((id) => {
    try { if (id) localStorage.setItem(chave, String(id)); else localStorage.removeItem(chave); } catch { /* ignora */ }
  }, [chave]);

  const avisar = useCallback((texto, acao) => {
    clearTimeout(avisoTimer.current);
    setAviso({ texto, acao });
    avisoTimer.current = setTimeout(() => setAviso(null), 5000);
  }, []);

  const carregarLista = useCallback(async () => {
    const r = await api.get('/especificacoes');
    setLista(r.data || []);
    return r.data || [];
  }, []);

  const carregarDetalhe = useCallback(async (id) => {
    if (!id) { setAtiva(null); return null; }
    setCarregandoAtiva(true);
    try {
      const r = await api.get(`/especificacoes/${id}`);
      setAtiva(r.data);
      return r.data;
    } catch {
      setAtiva(null); lembrar(null);
      return null;
    } finally { setCarregandoAtiva(false); }
  }, [lembrar]);

  // Boot: carrega a lista e a especificação ativa
  useEffect(() => {
    if (!habilitado) { setLista([]); setAtiva(null); return undefined; }
    let vivo = true;
    (async () => {
      try {
        const l = await carregarLista();
        if (!vivo) return;
        let salva = null;
        try { salva = Number(localStorage.getItem(chave)) || null; } catch { /* ignora */ }
        const doCatalogo = l.filter(e => e.origem !== 'pdf');
        const escolhida = doCatalogo.find(e => e.id === salva) || doCatalogo.find(e => e.status === 'rascunho');
        if (escolhida) { lembrar(escolhida.id); await carregarDetalhe(escolhida.id); }
      } catch { /* sem especificação ainda — segue */ }
    })();
    return () => { vivo = false; };
  }, [habilitado, chave, carregarLista, carregarDetalhe, lembrar]);

  const escolher = useCallback(async (id) => { lembrar(id); return carregarDetalhe(id); }, [lembrar, carregarDetalhe]);

  // Cria a especificação. Pelo catálogo ela já vira a ativa; a de PDF não.
  // Recarregar a lista é "extra": se a rede falhar ali, a especificação JÁ foi
  // criada — não pode virar erro (senão a pessoa clica de novo e duplica).
  const criar = useCallback(async (nome, extra = {}) => {
    const r = await api.post('/especificacoes', { nome, ...extra });
    try { await carregarLista(); } catch { /* atualiza na próxima */ }
    if (extra.origem !== 'pdf') await escolher(r.data.id);
    return r.data.id;
  }, [carregarLista, escolher]);

  const recarregar = useCallback(async () => {
    try { await Promise.all([carregarLista(), ativaRef.current ? carregarDetalhe(ativaRef.current.id) : null]); }
    catch { /* falha de rede: mantém o que já está na tela */ }
  }, [carregarLista, carregarDetalhe]);

  const adicionar = useCallback(async ({ especId, codpro, quantidade, ambientes, observacao }) => {
    const r = await api.post(`/especificacoes/${especId}/itens`, { codpro, quantidade, ambientes, observacao });
    lembrar(especId);
    await Promise.all([carregarDetalhe(especId), carregarLista()]);
    return r.data;
  }, [lembrar, carregarDetalhe, carregarLista]);

  // Atualiza na tela na hora (otimista) e grava no servidor com um respiro de
  // 600 ms — clicar várias vezes no "+" vira uma gravação só.
  const atualizarItem = useCallback((itemId, dados) => {
    const espId = ativaRef.current?.id;
    if (!espId) return;
    setAtiva(a => (a ? { ...a, itens: a.itens.map(i => (i.id === itemId ? { ...i, ...dados } : i)) } : a));
    const p = pendentes.current[itemId] || { dados: {} };
    clearTimeout(p.timer);
    p.dados = { ...p.dados, ...dados };
    p.timer = setTimeout(async () => {
      const envio = p.dados;
      delete pendentes.current[itemId];
      try { await api.put(`/especificacoes/${espId}/itens/${itemId}`, envio); }
      catch (e) {
        avisar(e.response?.data?.error || 'Não foi possível salvar a alteração');
        carregarDetalhe(espId);
      }
    }, 600);
    pendentes.current[itemId] = p;
  }, [avisar, carregarDetalhe]);

  const removerItem = useCallback(async (itemId) => {
    const espId = ativaRef.current?.id;
    if (!espId) return;
    const antes = ativaRef.current;
    setAtiva(a => (a ? { ...a, itens: a.itens.filter(i => i.id !== itemId) } : a));
    try { await api.delete(`/especificacoes/${espId}/itens/${itemId}`); carregarLista(); }
    catch (e) { setAtiva(antes); avisar(e.response?.data?.error || 'Não foi possível remover'); }
  }, [avisar, carregarLista]);

  const salvarLivre = useCallback(async (dados, itemId) => {
    const espId = ativaRef.current?.id;
    if (itemId) await api.put(`/especificacoes/${espId}/itens/${itemId}`, dados);
    else await api.post(`/especificacoes/${espId}/itens/livre`, dados);
    await Promise.all([carregarDetalhe(espId), carregarLista()]);
  }, [carregarDetalhe, carregarLista]);

  const atualizarEsp = useCallback(async (dados) => {
    const espId = ativaRef.current?.id;
    if (!espId) return;
    await api.put(`/especificacoes/${espId}`, dados);
    setAtiva(a => (a ? { ...a, ...dados } : a));
    carregarLista();
  }, [carregarLista]);

  const apagarEsp = useCallback(async (id) => {
    await api.delete(`/especificacoes/${id}`);
    if (ativaRef.current?.id === id) { setAtiva(null); lembrar(null); }
    await carregarLista();
  }, [lembrar, carregarLista]);

  const duplicarEsp = useCallback(async (id) => {
    const r = await api.post(`/especificacoes/${id}/duplicar`);
    await carregarLista();
    return r.data.id;
  }, [carregarLista]);

  const itemDoProduto = useCallback((codpro) => (ativa?.itens || []).find(i => i.codpro === codpro) || null, [ativa]);

  const listaCatalogo = useMemo(() => lista.filter(e => e.origem !== 'pdf'), [lista]);

  const valor = useMemo(() => ({
    habilitado, lista, listaCatalogo, ativa, carregandoAtiva,
    escolher, criar, recarregar, adicionar, atualizarItem, removerItem, salvarLivre, atualizarEsp, apagarEsp, duplicarEsp, itemDoProduto,
    cestaAberta, abrirCesta: () => setCestaAberta(true), fecharCesta: () => setCestaAberta(false),
    produtoAdd, abrirAdicionar: (p) => setProdutoAdd(p), fecharAdicionar: () => setProdutoAdd(null),
    livre, abrirLivre: (item) => setLivre({ item: item || null }), fecharLivre: () => setLivre(null),
    aviso, avisar, fecharAviso: () => setAviso(null),
    totalItens: ativa?.itens?.length || 0,
  }), [habilitado, lista, listaCatalogo, ativa, carregandoAtiva, escolher, criar, recarregar, adicionar, atualizarItem, removerItem,
    salvarLivre, atualizarEsp, apagarEsp, duplicarEsp, itemDoProduto, cestaAberta, produtoAdd, livre, aviso, avisar]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export const useEspecificacao = () => useContext(Ctx);
